import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, DollarSign, Mail, User, CreditCard } from 'lucide-react';
import axios from 'axios';
import Nav from '../components/Nav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonatePage = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    shirtSize: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Scroll to top when page loads
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const presetAmounts = [10, 25, 50, 100, 250, 500];

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setAmount(value);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const donationAmount = parseFloat(amount || customAmount);
    
    if (!donationAmount || donationAmount < 1) {
      setError('Please enter a valid donation amount (minimum $1)');
      return;
    }

    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const donationData = {
        name: formData.name,
        email: formData.email,
        amount: donationAmount,
        message: formData.message || 'No message provided',
        shirtSize: donationAmount >= 100 ? formData.shirtSize : ''
      };

      const response = await axios.post(`${API}/donations`, donationData);
      
      if (response.data) {
        // Navigate to receipt page with donation details
        navigate('/receipt', { 
          state: { 
            donation: response.data,
            type: 'donation'
          } 
        });
      }
    } catch (err) {
      console.error('Donation error:', err);
      setError('There was an error processing your donation. Please try again or contact dionlovellebooking@gmail.com directly.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Nav />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <Heart size={50} className="mx-auto mb-4 sm:mb-6 text-purple-400 sm:w-[60px] sm:h-[60px]" />
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 gradient-text px-2" data-testid="donate-title">Support My Music</h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-4 sm:mb-6 px-4">
            Your contribution helps bring more music to life through better production, equipment, and creative projects.
          </p>
          <p className="text-base sm:text-lg text-gray-400 px-4">
            All proceeds go to future projects, production, equipment, and overall artist development. 🎵
          </p>
        </div>
      </section>

      {/* Special Bonus Section */}
      <section className="py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card p-4 sm:p-6 md:p-8 bg-gradient-to-r from-purple-500/10 to-gold-500/10 border-2 border-purple-500/30">
            <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6">
              <div className="md:w-1/3 flex-shrink-0">
                <img 
                  src="/music-is-love-logo.png" 
                  alt="Music is Love"
                  className="w-full max-w-[200px] mx-auto"
                />
              </div>
              <div className="md:w-2/3 text-center md:text-left">
                <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-sm font-bold px-4 py-1 rounded-full mb-3">
                  ✨ SPECIAL BONUS ✨
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 gradient-text">Donate $100+ & Receive Exclusive Gifts!</h3>
                <ul className="space-y-2 text-base sm:text-lg">
                  <li className="flex items-center gap-2 justify-center md:justify-start flex-wrap">
                    <span className="text-xl sm:text-2xl">🎵</span>
                    <span className="break-words"><strong>Unreleased Acoustic Album</strong> (Digital Download)</span>
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start flex-wrap">
                    <span className="text-xl sm:text-2xl">👕</span>
                    <span className="break-words"><strong>&ldquo;Music is Love&rdquo; T-Shirt</strong> (Shipped to You)</span>
                  </li>
                </ul>
                <p className="text-xs sm:text-sm text-gray-400 mt-4">
                  Exclusive merch & unreleased music only for supporters who donate $100 or more!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Options - Big Buttons */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center gradient-text">Choose Your Payment Method</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Card Payment via Stripe */}
            <button
              onClick={() => document.getElementById('donation-form').scrollIntoView({ behavior: 'smooth' })}
              className="glass-card p-6 sm:p-8 hover:bg-purple-500/20 transition-all hover:scale-105 group cursor-pointer"
              data-testid="stripe-card-button"
            >
              <div className="flex flex-col items-center text-center">
                <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">💳</div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-purple-400 transition-colors">Credit/Debit Card</h3>
                <p className="text-sm sm:text-base text-gray-400">Secure payment via Stripe</p>
              </div>
            </button>

            {/* CashApp */}
            <a
              href="https://cash.app/$dionlovelle"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 sm:p-8 hover:bg-green-500/20 transition-all hover:scale-105 group"
              data-testid="cashapp-big-button"
            >
              <div className="flex flex-col items-center text-center">
                <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">💵</div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-green-400 transition-colors">CashApp</h3>
                <p className="text-sm sm:text-base text-gray-400 break-words">$dionlovelle</p>
              </div>
            </a>

            {/* Venmo */}
            <a
              href="https://venmo.com/dionlovelle"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 sm:p-8 hover:bg-blue-500/20 transition-all hover:scale-105 group"
              data-testid="venmo-big-button"
            >
              <div className="flex flex-col items-center text-center">
                <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">💙</div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-blue-400 transition-colors">Venmo</h3>
                <p className="text-sm sm:text-base text-gray-400 break-words">@dionlovelle</p>
              </div>
            </a>

            {/* PayPal */}
            <a
              href="https://paypal.me/dionlovelle"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 sm:p-8 hover:bg-blue-600/20 transition-all hover:scale-105 group"
              data-testid="paypal-big-button"
            >
              <div className="flex flex-col items-center text-center">
                <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">💰</div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-blue-300 transition-colors">PayPal</h3>
                <p className="text-sm sm:text-base text-gray-400 break-words">paypal.me/dionlovelle</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Card Donation Form */}
      <section className="py-8 sm:py-12 px-4 sm:px-6" id="donation-form">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-4 sm:p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard size={32} className="text-purple-400" />
              </div>
              <h2 className="text-3xl font-bold gradient-text">Card Payment</h2>
              <p className="text-gray-400 mt-2">Secure donation via Stripe</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Amount Selection */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-4 text-center">Choose Your Contribution</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handleAmountSelect(preset)}
                      className={`glass-card p-3 sm:p-4 text-center transition-all ${
                        amount === preset
                          ? 'border-2 border-purple-500 bg-purple-500/20'
                          : 'hover:border-purple-500/50'
                      }`}
                      data-testid={`amount-${preset}`}
                    >
                      <span className="text-xl sm:text-2xl font-bold gradient-text">${preset}</span>
                    </button>
                  ))}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <DollarSign size={18} />
                    Custom Amount
                  </label>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={handleCustomAmount}
                    min="1"
                    step="0.01"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Enter custom amount"
                    data-testid="custom-amount"
                  />
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <User size={18} />
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="John Doe"
                    required
                    data-testid="donor-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <Mail size={18} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="your@email.com"
                    required
                    data-testid="donor-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors resize-none"
                    placeholder="Leave a message of support..."
                    data-testid="donor-message"
                  />
                </div>

                {/* Shirt Size Selection (Only for $100+) */}
                {parseFloat(amount || customAmount) >= 100 && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                      <span className="text-xl">👕</span>
                      T-Shirt Size *
                    </label>
                    <select
                      name="shirtSize"
                      value={formData.shirtSize}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                      required={parseFloat(amount || customAmount) >= 100}
                      data-testid="shirt-size"
                    >
                      <option value="">Select your size</option>
                      <option value="Small">Small</option>
                      <option value="Medium">Medium</option>
                      <option value="Large">Large</option>
                      <option value="X-Large">X-Large</option>
                      <option value="2X-Large">2X-Large</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-2">
                      Required for your &ldquo;Music is Love&rdquo; t-shirt
                    </p>
                  </div>
                )}
              </div>

              {/* Summary */}
              {(amount || customAmount) && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                  <h3 className="font-bold text-lg mb-2">Donation Summary</h3>
                  <p className="flex justify-between text-2xl">
                    <span className="text-gray-400">Amount:</span>
                    <span className="font-bold gradient-text">${parseFloat(amount || customAmount).toFixed(2)}</span>
                  </p>
                  
                  {parseFloat(amount || customAmount) >= 100 && (
                    <div className="mt-4 pt-4 border-t border-purple-500/30">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">
                        ✨ BONUS INCLUDED ✨
                      </div>
                      <ul className="space-y-1 text-sm">
                        <li className="flex items-center gap-2">
                          <span>🎵</span>
                          <span>Unreleased Acoustic Album</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span>👕</span>
                          <span>&ldquo;Music is Love&rdquo; T-Shirt</span>
                        </li>
                      </ul>
                      <p className="text-xs text-gray-400 mt-2">
                        You&apos;ll receive instructions via email after donation!
                      </p>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm text-yellow-200">
                <div className="flex items-start gap-2">
                  <CreditCard size={18} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="break-words">
                      Secure payment processed via Stripe. You&apos;ll receive a receipt at your email and confirmation will be sent to{' '}
                      <strong className="break-all">dionlovellebooking@gmail.com</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing || !amount}
                className="w-full glow-button text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="donate-submit-button"
              >
                <Heart size={20} />
                {isProcessing ? 'Processing...' : `Donate $${parseFloat(amount || customAmount || 0).toFixed(2)}`}
              </button>

              <p className="text-center text-sm text-gray-400">
                All proceeds support future projects, production, equipment, and artist development
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Questions Section */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto text-center glass-card p-6">
          <p className="text-gray-400 mb-4">
            Questions about donations or bonus gifts?
          </p>
          <a 
            href="mailto:dionlovellebooking@gmail.com"
            className="text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-2"
          >
            <Mail size={18} />
            dionlovellebooking@gmail.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Dionlovelle. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DonatePage;
